// Slim transcript viewer for investigation calls and video: native audio
// element (or any shared media element, including a <video>) + synced
// segments, click-to-seek, English/native toggle. Implements the same
// onWordClick(time)/currentTime contract as the case editor's Transcription
// component without its 3,000 lines of case_files wiring.
import { forwardRef, useLayoutEffect, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { PlayIcon, PauseIcon, BackwardIcon, ForwardIcon } from "@heroicons/react/24/solid";
import cn from "classnames";
import AiDisclaimer from "./AiDisclaimer";
import Dropdown from "../Settings/Dropdown";
import { CallParticipant, InvestigationLead } from "../../db/schema";
import { FlagIcon } from "@heroicons/react/24/solid";
import { LEAD_CATEGORY_LABELS, NormalizedTranscript, SpeakerSuggestion, formatMs } from "./api";
import LeadFlagPopover, { LeadHover, PopoverItem } from "./LeadFlagPopover";
import { kindStyle } from "../common/video/types";
import { scrollWithin } from "../../utilities/scrollWithin";

export type TranscriptHandle = { seek: (ms: number, play?: boolean) => void };

// A flagged span in the transcript: an investigation lead, a video event,
// or a footage-check finding. Rows overlapping [startMs, endMs] are tinted
// and show the card on hover.
export type TranscriptHighlight = {
  id: string;
  startMs: number;
  endMs: number;
  severity: number;
  kind?: string;
  label: string;
  quote: string;
  explanation?: string;
  corner?: React.ReactNode;
  dismissed?: boolean;
  frameT?: number; // still to show in the card
};

interface Props {
  transcript: NormalizedTranscript;
  audioUrl: string;
  participants?: CallParticipant[] | null;
  // Flagged moments; a segment containing a lead's start_ms is highlighted
  // and shows the lead on hover.
  flags?: InvestigationLead[];
  // The generic form of `flags`, for surfaces without lead rows (video
  // events, footage-check findings). When both are given, both show.
  highlights?: TranscriptHighlight[];
  onRenameSpeaker?: (label: string) => void;
  suggestions?: SpeakerSuggestion[];
  onConfirmSuggestion?: (s: SpeakerSuggestion) => void;
  onDismissSuggestion?: (s: SpeakerSuggestion) => void;
  initialSeekMs?: number;
  // sessionStorage key for remembering the transcript's scroll position, so
  // returning to the call (history back) lands where the detective was.
  scrollKey?: string;
  // Shared element from NowPlayingContext. When given, no <audio> is rendered
  // here and playback continues after this component unmounts.
  audio?: HTMLAudioElement;
  // Any media element (a <video> in the video view). Alias of `audio`.
  media?: HTMLMediaElement;
  // The video view draws its own controls under the player.
  hideControls?: boolean;
  // Still lookup for the hover card (video surfaces).
  frameUrl?: (t: number) => string;
}

const SPEAKER_COLORS = ["bg-blue-500", "bg-green-500", "bg-purple-500", "bg-orange-500", "bg-red-500", "bg-pink-500"];

function leadToHighlight(l: InvestigationLead): TranscriptHighlight {
  return {
    id: `lead:${l.id}`,
    startMs: l.start_ms || 0,
    endMs: l.end_ms != null && l.end_ms > (l.start_ms || 0) ? l.end_ms : l.start_ms || 0,
    severity: l.severity,
    kind: l.category,
    label: LEAD_CATEGORY_LABELS[l.category] || l.category,
    quote: l.quote_translated || l.quote,
    explanation: l.explanation || undefined,
    corner: formatMs(l.start_ms),
    dismissed: l.status === "dismissed",
  };
}

const InvestigationTranscript = forwardRef<TranscriptHandle, Props>(({ transcript, audioUrl, participants, flags = [], highlights, onRenameSpeaker, suggestions = [], onConfirmSuggestion, onDismissSuggestion, initialSeekMs, scrollKey, audio, media, hideControls, frameUrl }, ref) => {
  const shared: HTMLMediaElement | undefined = media ?? audio;
  const audioRef = useRef<HTMLAudioElement>(null);
  const getEl = (): HTMLMediaElement | null => shared ?? audioRef.current;
  const listRef = useRef<HTMLDivElement>(null);
  // Set by seek() and the scroll restore; the follow scroll yields for a
  // moment so a deliberate placement isn't cancelled by a "nearest" scroll.
  const jumpAt = useRef(0);
  const [currentMs, setCurrentMs] = useState(() => (shared ? Math.round(shared.currentTime * 1000) : 0));
  const [playing, setPlaying] = useState(() => Boolean(shared && !shared.paused));
  const [showNative, setShowNative] = useState(false);
  const [follow, setFollow] = useState(true);
  const [rate, setRate] = useState(() => shared?.playbackRate || 1);

  // Mirror the shared element's state (the inline <audio> uses JSX handlers).
  useEffect(() => {
    if (!shared) return;
    const onTime = () => setCurrentMs(Math.round(shared.currentTime * 1000));
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onRate = () => setRate(shared.playbackRate);
    shared.addEventListener("timeupdate", onTime);
    shared.addEventListener("seeked", onTime);
    shared.addEventListener("play", onPlay);
    shared.addEventListener("pause", onPause);
    shared.addEventListener("ended", onPause);
    shared.addEventListener("ratechange", onRate);
    onTime();
    setPlaying(!shared.paused);
    return () => {
      shared.removeEventListener("timeupdate", onTime);
      shared.removeEventListener("seeked", onTime);
      shared.removeEventListener("play", onPlay);
      shared.removeEventListener("pause", onPause);
      shared.removeEventListener("ended", onPause);
      shared.removeEventListener("ratechange", onRate);
    };
  }, [shared]);
  const isEnglish = /^en/i.test(transcript.primaryLanguage);
  const hasTranslation = transcript.segments.some((s) => s.english);

  const speakers = useMemo(() => {
    const labels = [...new Set(transcript.segments.map((s) => s.speaker))].sort();
    return Object.fromEntries(
      labels.map((label, i) => {
        const p = participants?.find((x) => x.label === label);
        const name = p?.name || (p?.role && p.role !== "unknown" ? p.role.replace("_", " ") : null) || `Speaker ${i + 1}`;
        return [label, { name, color: SPEAKER_COLORS[i % SPEAKER_COLORS.length] }];
      })
    );
  }, [transcript.segments, participants]);

  const seek = (ms: number, play = true) => {
    const el = getEl();
    if (!el) return;
    el.currentTime = ms / 1000;
    setCurrentMs(ms);
    if (play) el.play().catch(() => {});
    // A deliberate jump (flag, glossary term, citation) lands the line mid-pane
    // so there's context above and below it. Playback follow uses "nearest"
    // and won't fight this since the line is already in view.
    const idx = transcript.segments.findIndex((s) => ms >= s.startMs && ms < s.endMs);
    const target = idx >= 0 ? idx : transcript.segments.findIndex((s) => s.startMs > ms) - 1;
    if (target >= 0) {
      jumpAt.current = Date.now();
      scrollWithin(listRef.current, listRef.current?.querySelector<HTMLElement>(`[data-seg="${target}"]`) || null);
    }
  };
  useImperativeHandle(ref, () => ({ seek }));

  useEffect(() => {
    // With the shared element the owner (CallDetail / VideoView) handles the initial seek.
    if (shared) return;
    if (initialSeekMs != null && audioRef.current) {
      const el = audioRef.current;
      const h = () => seek(initialSeekMs, false);
      if (el.readyState >= 1) h();
      else el.addEventListener("loadedmetadata", h, { once: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSeekMs, audioUrl]);

  // Restore the remembered position once on mount (a citation/flag jump via
  // initialSeekMs takes precedence). The follow scroll yields briefly so it
  // doesn't immediately drag the pane to the current line.
  useLayoutEffect(() => {
    if (!scrollKey || initialSeekMs != null || !listRef.current) return;
    const saved = Number(sessionStorage.getItem(scrollKey) || 0);
    if (saved > 0) {
      listRef.current.scrollTop = saved;
      jumpAt.current = Date.now();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollKey]);
  const onListScroll = () => {
    if (scrollKey && listRef.current) sessionStorage.setItem(scrollKey, String(listRef.current.scrollTop));
  };

  const activeIndex = useMemo(() => {
    const idx = transcript.segments.findIndex((s) => currentMs >= s.startMs && currentMs < s.endMs);
    return idx;
  }, [transcript.segments, currentMs]);

  useEffect(() => {
    if (!follow || activeIndex < 0 || !listRef.current) return;
    if (Date.now() - jumpAt.current < 1200) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-seg="${activeIndex}"]`);
    scrollWithin(listRef.current, el);
  }, [activeIndex, follow]);

  const allHighlights = useMemo<TranscriptHighlight[]>(
    () => [...flags.map(leadToHighlight), ...(highlights || [])].filter((h) => !h.dismissed),
    [flags, highlights]
  );

  // Map each segment index → the highlights that fall inside it. A quote often
  // spans more than one transcript segment (the transcriber splits a single
  // utterance at pauses), so every segment overlapping the highlight's time
  // range is flagged, not just the one containing its start.
  const flagsBySegment = useMemo(() => {
    const map = new Map<number, TranscriptHighlight[]>();
    const segs = transcript.segments;
    for (const f of allHighlights) {
      const startMs = f.startMs || 0;
      const endMs = f.endMs > startMs ? f.endMs : startMs;
      let matched = false;
      segs.forEach((s, idx) => {
        // Overlap test with an open end so a lead starting exactly where a
        // segment ends doesn't drag that earlier segment in.
        if (s.startMs < endMs + 1 && s.endMs > startMs) {
          map.set(idx, [...(map.get(idx) || []), f]);
          matched = true;
        }
      });
      if (matched) continue;
      // Lead sits in a gap between segments: attach to the next one.
      const idx = segs.findIndex((s) => s.startMs >= startMs);
      if (idx >= 0) map.set(idx, [...(map.get(idx) || []), f]);
    }
    return map;
  }, [allHighlights, transcript.segments]);

  const [hoverFlag, setHoverFlag] = useState<LeadHover | null>(null);
  const toItem = (h: TranscriptHighlight): PopoverItem => ({
    id: h.id,
    severity: h.severity,
    label: h.label,
    corner: h.corner ?? formatMs(h.startMs),
    quote: h.quote,
    explanation: h.explanation,
    kind: h.kind,
    thumbnailUrl: frameUrl && h.frameT != null ? frameUrl(h.frameT) : undefined,
  });

  return (
    <div className="flex flex-col h-full min-h-0">
      {!shared && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="metadata"
          onTimeUpdate={(e) => setCurrentMs(Math.round(e.currentTarget.currentTime * 1000))}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onRateChange={(e) => setRate(e.currentTarget.playbackRate)}
        />
      )}
      {!hideControls && (
        <div className="border-b border-base-300 px-3 py-2 flex items-center gap-2 flex-wrap">
          {/* Controls mirror the case sidebar MediaPlayer: blue circular play,
              secondary-colored ±10s, blue/grey seek bar with a white dot. */}
          <button
            type="button"
            className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
            onClick={() => seek(Math.max(0, currentMs - 10000))}
            title="Back 10s"
          >
            <BackwardIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-colors"
            onClick={() => (playing ? getEl()?.pause() : getEl()?.play().catch(() => {}))}
            title={playing ? "Pause" : "Play"}
          >
            {playing ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4 ml-0.5" />}
          </button>
          <button
            type="button"
            className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
            onClick={() => seek(currentMs + 10000)}
            title="Forward 10s"
          >
            <ForwardIcon className="h-4 w-4" />
          </button>
          <span className="text-sm tabular-nums text-base-content/70">
            {formatMs(currentMs)} / {formatMs(transcript.durationMs)}
          </span>
          <input
            type="range"
            min={0}
            max={transcript.durationMs || 1}
            value={currentMs}
            onChange={(e) => seek(Number(e.target.value), playing)}
            className="inv-seek flex-1 min-w-32"
            style={{ "--p": `${transcript.durationMs ? (currentMs / transcript.durationMs) * 100 : 0}%` } as React.CSSProperties}
          />
          <Dropdown
            size="xs"
            contentClassName="w-20"
            options={[1, 1.25, 1.5, 2].map((r) => ({ value: String(r), label: `${r}×` }))}
            value={String(rate)}
            onChange={(v) => { const r = Number(v); const el = getEl(); if (el) el.playbackRate = r; setRate(r); }}
          />
          <label className="label cursor-pointer gap-1 text-xs">
            <input type="checkbox" className="checkbox checkbox-xs" checked={follow} onChange={(e) => setFollow(e.target.checked)} /> Follow
          </label>
          {hasTranslation && !isEnglish && (
            <label className="label cursor-pointer gap-1 text-xs">
              <input type="checkbox" className="checkbox checkbox-xs" checked={showNative} onChange={(e) => setShowNative(e.target.checked)} /> Show original
            </label>
          )}
        </div>
      )}
      <div className="px-3 py-1.5 border-b border-base-300 flex flex-wrap gap-2 text-xs">
        {Object.entries(speakers).map(([label, s]) => {
          const sug = suggestions.find((x) => x.label === label);
          return (
            <span key={label} className="inline-flex items-center gap-1.5">
              <button className="inline-flex items-center gap-1 hover:underline" onClick={() => onRenameSpeaker?.(label)} title="Rename speaker">
                <span className={cn("size-2.5 rounded-full", s.color)} /> {s.name}
                <span className="text-base-content/40">({label})</span>
              </button>
              {sug && (
                // Same-number guess: shown as a question until the detective answers it.
                <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-900/20 pl-2 pr-1 py-0.5" title={`${sug.source === "detective" ? "Identified" : "Heard"} on ${sug.support} other call${sug.support === 1 ? "" : "s"} to this number${sug.conflicts.length ? ` · also heard: ${sug.conflicts.join(", ")}` : ""}`}>
                  <span className="text-base-content/70">Probably <span className="font-semibold text-base-content">{sug.name}</span>?</span>
                  <button className="px-1.5 rounded-full bg-blue-500 text-white hover:bg-blue-600 text-[11px] font-medium" onClick={() => onConfirmSuggestion?.(sug)}>Confirm</button>
                  <button className="px-1.5 rounded-full text-base-content/60 hover:bg-base-200 text-[11px]" onClick={() => onDismissSuggestion?.(sug)}>Not them</button>
                </span>
              )}
            </span>
          );
        })}
        {hideControls && (
          <label className="label cursor-pointer gap-1 text-xs ml-auto py-0">
            <input type="checkbox" className="checkbox checkbox-xs" checked={follow} onChange={(e) => setFollow(e.target.checked)} /> Follow
          </label>
        )}
        {hideControls && hasTranslation && !isEnglish && (
          <label className="label cursor-pointer gap-1 text-xs py-0">
            <input type="checkbox" className="checkbox checkbox-xs" checked={showNative} onChange={(e) => setShowNative(e.target.checked)} /> Show original
          </label>
        )}
        {transcript.preambleEndMs > 0 && <span className="text-base-content/40">· preamble ends {formatMs(transcript.preambleEndMs)}</span>}
      </div>
      <div ref={listRef} className="flex-1 overflow-auto px-3 py-2 space-y-1.5" onScroll={onListScroll}>
        <AiDisclaimer kind="transcript" size="sm" className="mb-2" />
        {transcript.segments.map((seg, i) => {
          const s = speakers[seg.speaker];
          const active = i === activeIndex;
          const isPreamble = transcript.preambleEndMs > 0 && seg.endMs <= transcript.preambleEndMs;
          const segFlags = flagsBySegment.get(i);
          const flagged = Boolean(segFlags?.length);
          const top = segFlags ? segFlags.reduce((a, f) => (f.severity > a.severity ? f : a), segFlags[0]) : null;
          const topSeverity = top?.severity || 0;
          // Video events carry a kind; leads and findings tint by severity.
          const kindTint = top?.kind && top.severity === 3 && !flags.length ? kindStyle(top.kind) : null;
          // Rows that share a lead with their neighbour are drawn as one
          // continuous block (no gap, no inner corners, one flag) while each
          // row keeps its own hover/click.
          const shares = (other: number) => {
            const o = flagsBySegment.get(other);
            return Boolean(o?.some((f) => segFlags?.some((g) => g.id === f.id)));
          };
          const joinPrev = flagged && shares(i - 1);
          const joinNext = flagged && shares(i + 1);
          return (
            <div
              key={seg.id}
              data-seg={i}
              onClick={() => seek(seg.startMs)}
              onMouseEnter={(e) => {
                if (!segFlags?.length) return;
                const r = e.currentTarget.getBoundingClientRect();
                const below = r.bottom + 220 < window.innerHeight;
                setHoverFlag({ leads: [], items: segFlags.map(toItem), x: Math.min(r.left + 56, window.innerWidth - 400), y: below ? r.bottom + 6 : r.top - 6, below });
              }}
              onMouseLeave={() => setHoverFlag(null)}
              className={cn(
                "relative flex gap-2 rounded-lg px-2 py-1 cursor-pointer transition-colors",
                // Reserve room on the right for the absolutely-positioned flag
                // so long lines wrap instead of running underneath it.
                flagged && "pr-8",
                joinPrev && "!mt-0 rounded-t-none",
                joinNext && "rounded-b-none",
                isPreamble && "opacity-50",
                // Unflagged: blue when playing, neutral hover otherwise.
                !flagged && (active ? "bg-blue-50 dark:bg-blue-900/40" : "hover:bg-base-200"),
                // Flagged: a deeper shade of the same hue while playing, so the
                // flag stays legible and the playhead is still obvious.
                flagged && !kindTint && topSeverity >= 4 && (active ? "bg-red-200 dark:bg-red-900/60 ring-1 ring-inset ring-red-300 dark:ring-red-800" : "bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50"),
                flagged && !kindTint && topSeverity < 4 && (active ? "bg-amber-200 dark:bg-amber-900/60 ring-1 ring-inset ring-amber-300 dark:ring-amber-800" : "bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-950/50"),
                flagged && kindTint && cn(kindTint.tint, active && cn("ring-1 ring-inset", kindTint.ring))
              )}
            >
              {flagged && (
                <>
                  {/* Straight accent bar (not a border, so it ignores the row's radius). */}
                  <span className={cn("absolute left-0 top-0 bottom-0 w-1 rounded-sm", joinPrev && "rounded-t-none", joinNext && "rounded-b-none", kindTint ? kindTint.dot : topSeverity >= 4 ? "bg-red-500" : "bg-amber-500")} />
                  {!joinPrev && <FlagIcon className={cn("absolute right-2 top-1.5 size-3.5", kindTint ? kindTint.text : topSeverity >= 4 ? "text-red-500" : "text-amber-500")} />}
                </>
              )}
              {/* Same 20px line box as the text so it centers on the first line */}
              <span className="text-[11px] leading-5 tabular-nums text-base-content/50 w-10 shrink-0 pt-[3px]">{formatMs(seg.startMs)}</span>
              {/* 10px dot centered on the 20px first line */}
              <span className={cn("size-2.5 rounded-full shrink-0 mt-[8.5px]", s?.color)} />
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  className={cn("text-xs font-semibold text-base-content/70 mr-1 rounded px-0.5 -mx-0.5", onRenameSpeaker && "hover:text-blue-600 dark:hover:text-blue-300 hover:bg-base-100/80")}
                  title={onRenameSpeaker ? "Identify this speaker" : undefined}
                  onClick={(e) => {
                    if (!onRenameSpeaker) return;
                    e.stopPropagation();
                    onRenameSpeaker(seg.speaker);
                  }}
                >
                  {s?.name}:
                </button>
                <span className="text-sm text-base-content">{!isEnglish && seg.english && !showNative ? seg.english : seg.text}</span>
                {!isEnglish && seg.english && showNative && <div className="text-xs text-base-content/60 italic">{seg.english}</div>}
              </div>
            </div>
          );
        })}
      </div>
      {hoverFlag && <LeadFlagPopover hover={hoverFlag} />}
    </div>
  );
});

export default InvestigationTranscript;
